import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AppStateContext from "contexts/AppStateContext";
import { useToast } from "contexts/ToastContext";
import { getSettingByName, updateSetting } from "util/network/Settings";

// Message block on the webshop, at the top of every page or on the basket page
// (EE-295). It replaces the old global message. The backend validates the same
// shape and limits in modules/settings/message.py.
export const MESSAGE_SETTING = "MESSAGE";

type Language = "dk" | "gb";
type Texts = Record<Language, string>;
type Placement = "top" | "basket";
type Level = "info" | "warning" | "important";

export interface Message {
  active: boolean;
  placement: Placement;
  level: Level;
  title: Texts;
  text: Texts;
  link: string;
  link_text: Texts;
}

const EMPTY_TEXTS: Texts = { dk: "", gb: "" };
const EMPTY: Message = {
  active: false,
  placement: "top",
  level: "info",
  title: EMPTY_TEXTS,
  text: EMPTY_TEXTS,
  link: "",
  link_text: EMPTY_TEXTS,
};

const LANGUAGES: { code: Language; label: string }[] = [
  { code: "dk", label: "Danish" },
  { code: "gb", label: "English" },
];

const PLACEMENTS: { value: Placement; label: string; shown: string }[] = [
  { value: "top", label: "Top of every page", shown: "Shown at the top of every page" },
  { value: "basket", label: "Basket page", shown: "Shown on the basket page" },
];

// Same colours as the webshop's GlobalMessage.module.css.
const LEVELS: {
  value: Level;
  label: string;
  background: string;
  accent: string;
  Icon: typeof InfoOutlinedIcon;
}[] = [
  { value: "info", label: "Info", background: "#e5f3f2", accent: "#008b80", Icon: InfoOutlinedIcon },
  { value: "warning", label: "Warning", background: "#fdf3d6", accent: "#e1a900", Icon: WarningAmberOutlinedIcon },
  { value: "important", label: "Important", background: "#fce9ed", accent: "#e02c4f", Icon: ErrorOutlineIcon },
];

const MAX = { title: 100, text: 1000, link_text: 50, link: 500 };

const linkIsValid = (link: string) => {
  const trimmed = link.trim();
  return trimmed === "" || /^https?:\/\//.test(trimmed);
};

const Preview = ({ info, language }: { info: Message; language: Language }) => {
  const other: Language = language === "dk" ? "gb" : "dk";
  const pick = (texts: Texts) => texts[language] || texts[other];
  const text = pick(info.text);

  if (!text) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing to preview yet.
      </Typography>
    );
  }

  // Mirrors the webshop: a full-width bar at the top, a card on the basket page.
  const top = info.placement === "top";
  const level = LEVELS.find(({ value }) => value === info.level) ?? LEVELS[0];
  const { Icon, accent } = level;
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        justifyContent: top ? "center" : "flex-start",
        alignItems: "center",
        padding: "14px 16px",
        background: level.background,
        ...(top
          ? { borderBottom: `3px solid ${accent}` }
          : { borderTop: `3px solid ${accent}` }),
      }}
    >
      <Icon sx={{ color: accent, fontSize: 26 }} />
      <Box>
        {pick(info.title) && (
          <Typography fontWeight="bold">{pick(info.title)}</Typography>
        )}
        <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
          {text}
        </Typography>
        {info.link && (
          <Link
            href={info.link}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            underline="none"
            sx={{ color: "#008b80", fontWeight: 700, textTransform: "uppercase" }}
          >
            {pick(info.link_text) || (language === "dk" ? "Læs mere" : "Read more")} ›
          </Link>
        )}
      </Box>
    </Box>
  );
};

const MessageEditor = () => {
  const { token } = useContext(AppStateContext);
  const { addToast } = useToast();
  const [saved, setSaved] = useState<Message | null>(null);
  const [info, setInfo] = useState<Message>(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSettingByName(token, MESSAGE_SETTING)
      .then((setting) => {
        const value = { ...EMPTY, ...setting.value };
        setSaved(value);
        setInfo(value);
      })
      .catch((error) => {
        addToast({ type: "error", message: error.message });
      });
    // addToast is recreated on every render; loading once per token is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!saved) {
    return null;
  }

  const setText = (field: "title" | "text" | "link_text", language: Language, value: string) =>
    setInfo({ ...info, [field]: { ...info[field], [language]: value } });

  const hasText = LANGUAGES.some(({ code }) => info.text[code].trim() !== "");
  const isModified = JSON.stringify(info) !== JSON.stringify(saved);
  const canSave = isModified && linkIsValid(info.link) && (!info.active || hasText);

  const handleSave = async () => {
    setSaving(true);
    try {
      const setting = await updateSetting(token, MESSAGE_SETTING, info);
      // The backend trims the texts; show what was stored.
      setSaved(setting.value);
      setInfo(setting.value);
      addToast({ type: "success", message: "Message saved" });
    } catch (error) {
      addToast({
        type: "error",
        message: error instanceof Error ? error.message : "Unknown error occurred",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ paddingTop: "1.5rem", maxWidth: "1100px" }}>
      <Typography variant={"subtitle1"} fontWeight={"bold"}>
        Webshop message
      </Typography>
      <Typography variant={"body2"} style={{ paddingBottom: "0.5rem" }}>
        A message for customers, at the top of every webshop page (e.g. holiday
        delays) or on the basket page (e.g. news about a new release).
        Customers who close it see it again when the content changes.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={info.active}
            onChange={(event) => setInfo({ ...info, active: event.target.checked })}
            color="primary"
          />
        }
        label={
          info.active
            ? PLACEMENTS.find(({ value }) => value === info.placement)?.shown
            : "Hidden"
        }
      />
      <TextField
        select
        label="Placement"
        value={info.placement}
        onChange={(e) => setInfo({ ...info, placement: e.target.value as Placement })}
        size="small"
        sx={{ minWidth: 220, marginLeft: 1 }}
      >
        {PLACEMENTS.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Level"
        value={info.level}
        onChange={(e) => setInfo({ ...info, level: e.target.value as Level })}
        size="small"
        sx={{ minWidth: 160, marginLeft: 1 }}
      >
        {LEVELS.map(({ value, label, accent, Icon }) => (
          <MenuItem key={value} value={value}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Icon fontSize="small" sx={{ color: accent }} />
              {label}
            </Box>
          </MenuItem>
        ))}
      </TextField>

      <Grid container spacing={2} sx={{ paddingTop: 1 }}>
        {LANGUAGES.map(({ code, label }) => (
          <Grid item xs={12} md={6} key={code}>
            <Typography variant="body2" fontWeight="bold" sx={{ paddingBottom: 1 }}>
              {label}
            </Typography>
            <TextField
              label="Title (optional)"
              value={info.title[code]}
              onChange={(e) => setText("title", code, e.target.value)}
              inputProps={{ maxLength: MAX.title }}
              size="small"
              fullWidth
              sx={{ marginBottom: 1.5 }}
            />
            <TextField
              label="Text"
              value={info.text[code]}
              onChange={(e) => setText("text", code, e.target.value)}
              inputProps={{ maxLength: MAX.text }}
              helperText={`${info.text[code].length}/${MAX.text}`}
              size="small"
              multiline
              minRows={3}
              fullWidth
              sx={{ marginBottom: 1.5 }}
            />
            <TextField
              label="Link text (optional)"
              placeholder={code === "dk" ? "Læs mere" : "Read more"}
              value={info.link_text[code]}
              onChange={(e) => setText("link_text", code, e.target.value)}
              inputProps={{ maxLength: MAX.link_text }}
              size="small"
              fullWidth
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <TextField
            label="Link (optional)"
            placeholder="https://"
            value={info.link}
            onChange={(e) => setInfo({ ...info, link: e.target.value })}
            inputProps={{ maxLength: MAX.link }}
            error={!linkIsValid(info.link)}
            helperText={!linkIsValid(info.link) ? "Must start with https:// or http://" : " "}
            size="small"
            fullWidth
          />
        </Grid>
        {LANGUAGES.map(({ code, label }) => (
          <Grid item xs={12} md={6} key={code}>
            <Typography variant="body2" fontWeight="bold" sx={{ paddingBottom: 1 }}>
              Preview, {label}
            </Typography>
            <Preview info={info} language={code} />
          </Grid>
        ))}
      </Grid>

      {info.active && !hasText && (
        <Typography variant="body2" color="error" sx={{ paddingTop: 1 }}>
          Write the text in at least one language before switching it on.
        </Typography>
      )}
      <Box sx={{ display: "flex", gap: 1, paddingTop: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
          Save
        </Button>
        <Button onClick={() => setInfo(saved)} disabled={!isModified || saving}>
          Discard changes
        </Button>
      </Box>
    </div>
  );
};

export default MessageEditor;
